import {
  type ApiRequest,
  deleteAPI,
  properCallApi,
  type ApiResponseSingle,
  type ApiResponse
} from '@/api/tenantClient';

import { type DiscountCode } from './discountCode';
import { type RegistrationRouteName } from '@/registration/pages';
import { type RegistrationStrategyType } from './attendeeCategory';

export const registrationQueryKey = 'registration.registration';
export const registrationTableQueryKey = 'registration.table';

export type RegistrationSteps = RegistrationStep[];
export type RegistrationUpsellSteps = RegistrationUpsellStep[];

export interface RegistrationStep {
  routeName: RegistrationRouteName
  isEnable: boolean
}

export interface RegistrationUpsellStep {
  routeName: RegistrationRouteName
  isEnable: boolean
}

export interface AuthObject {
  uid: string
  client: string
  token: string
}

export interface Registration {
  id: number
  email: string
  firstName?: string
  lastName?: string
  attendeeCategoryId: number
  attendeeCategory: {
    id: number
    name: string
    invoiceEnabled: boolean
    creditCardEnabled: boolean
  }
  discountId?: number
  tmpIdPhotocopy?: string
  idPhotocopy?: string
  idInfo?: string
  expiry: string
  termsAndConditionsAccepted: boolean
  privacyPolicyAccepted: boolean
  eventId: string
  paid: boolean
  orderHash?: string
  orderHashUpsell?: string
  status: string
  event: {
    id: string
    name: string
  }
  userId: number
  createdAt: string
  personalInfoFormResponseCount: number
  additionalInfoFormResponseCount: number
  mainEventTicket: string
  otherTickets: Array<{ id: number, name: string, quantity: number }>
  registrationSteps: RegistrationSteps
  registrationUpsellSteps: RegistrationUpsellSteps
  registrationStrategy: RegistrationStrategyType
  discount: DiscountCode
  groupRegistrationId: number
}

export interface RegistrationTableRow {
  id: number
  uuid: number
  fullName: string
  email: string
  main_ticket: string
  status: string
  paid: string
  complete: string
  createdAt: string
  confirmDelegateTypeAt: string
}

export interface RegistrationTableQueryRequest {
  page: number
  filters?: any
  sorting?: any
  size: number
}

export interface RegistrationQueryRequest {
  email: string
  eventId: string
}

export interface RegistrationCompleteRequest {
  registrationId: number
}

export function loadRegistration(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (
    params: RegistrationQueryRequest
  ): Promise<ApiResponseSingle<Registration>> => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/registrations/load_registration';
    request.payload = {
      email: params.email,
      eventId: params.eventId
    };
    return await properCallApi<ApiResponseSingle<Registration>>(request);
  };
}

export interface RegistrationSaveRequest extends Omit<Registration, 'id' | 'expiry' | 'registrationSteps'> {
  eventId: string
}

export interface RegistrationDeleteRequest {
  id: string
}

export function deleteRegistration(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (params: RegistrationDeleteRequest) => {
    request.method = 'DELETE';
    request.endpoint.path = '/api/v1/registrations/' + params.id;
    await deleteAPI(request);
  };
}

export function completeRegistration(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (
    params: RegistrationCompleteRequest
  ): Promise<ApiResponseSingle<Registration>> => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/registrations/complete';
    request.payload = {
      id: params.registrationId
    };
    return await properCallApi<ApiResponseSingle<Registration>>(request);
  };
};

export interface RegistrationUpdateRequest {
  registration: {
    id: number
    attendeeCategoryId?: number
    discountId?: number
    tmpIdPhotocopy?: string
    idInfo?: string
    termsAndConditionsAccepted: boolean
    privacyPolicyAccepted: boolean
  }
}

export function updateRegistration(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (
    params: RegistrationUpdateRequest
  ): Promise<ApiResponseSingle<Registration>> => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/registrations/update';
    request.payload = params;

    return await properCallApi<ApiResponseSingle<Registration>>(request);
  };
};

export function loadRegistrationsForTable(createTenantAdminRequest: () => ApiRequest) {
  const request = createTenantAdminRequest();
  return async (
    params: RegistrationTableQueryRequest
  ): Promise<ApiResponse<RegistrationTableRow>> => {
    request.method = 'GET';
    request.endpoint.path = '/api/v1/registrations/table';
    request.endpoint.query = {
      page: (params?.page ?? 0) + 1,
      filters: JSON.stringify(params.filters ?? []),
      sorting: JSON.stringify(params.sorting ?? []),
      size: params.size ?? 10
    };

    return await properCallApi<ApiResponse<RegistrationTableRow>>(request);
  };
}
